import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { currencyApi } from '../api/services';
import type { CurrencyRate } from '../types';

interface CurrencyContextValue {
  currency: string;
  setCurrency: (code: string) => void;
  rates: CurrencyRate[];
  loading: boolean;
  convert: (amount: number, fromCurrency: string) => number | null;
  getRate: (code: string) => CurrencyRate | undefined;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const STORAGE_KEY = 'preferredCurrency';
const DEFAULT_CURRENCY = 'IRR';

function loadPreferredCurrency(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<string>(loadPreferredCurrency);
  const [rates, setRates] = useState<CurrencyRate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    currencyApi
      .getRates()
      .then(setRates)
      .catch(() => setRates([]))
      .finally(() => setLoading(false));
  }, []);

  const setCurrency = useCallback((code: string) => {
    setCurrencyState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // ignored
    }
  }, []);

  const rateMap = useMemo(() => {
    const m = new Map<string, CurrencyRate>();
    for (const r of rates) {
      if (!m.has(r.currency_code)) {
        m.set(r.currency_code, r);
      }
    }
    return m;
  }, [rates]);

  const getRate = useCallback((code: string) => rateMap.get(code), [rateMap]);

  const convert = useCallback(
    (amount: number, fromCurrency: string): number | null => {
      if (!amount || !fromCurrency) return null;
      if (fromCurrency === currency) return amount;

      if (fromCurrency === 'IRR') {
        const toRate = rateMap.get(currency);
        if (!toRate) return null;
        return Math.round(amount / toRate.rate_to_irr);
      }

      if (currency === 'IRR') {
        const fromRate = rateMap.get(fromCurrency);
        if (!fromRate) return null;
        return Math.round(amount * fromRate.rate_to_irr);
      }

      const fromRate = rateMap.get(fromCurrency);
      const toRate = rateMap.get(currency);
      if (!fromRate || !toRate) return null;

      const amountIrr = amount * fromRate.rate_to_irr;
      return Math.round(amountIrr / toRate.rate_to_irr);
    },
    [currency, rateMap]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rates, loading, convert, getRate }),
    [currency, setCurrency, rates, loading, convert, getRate]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}
